import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { MapPin, Briefcase, ExternalLink, Clock, Wifi, ArrowLeft, Eye } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Loader2 } from "lucide-react"
import { getJobBySlug, getUserById, incrementJobView } from "@/lib/db"
import { formatRelativeTime, formatDate, getInitials, formatNumber } from "@/lib/utils"
import { MOCK_JOBS } from "@/data/mockJobs"
import type { JobWithAuthor } from "@/types"

const jobTypeLabels: Record<string, string> = {
  "full-time": "Full-time",
  "part-time": "Part-time",
  contract: "Contract",
  freelance: "Freelance",
}

function formatSalary(min: number | null, max: number | null): string | null {
  if (!min && !max) return null
  const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
  if (min && max) return `${fmt(min)} – ${fmt(max)}`
  if (min) return `From ${fmt(min)}`
  return `Up to ${fmt(max!)}`
}

const recentViewIncrements = new Map<string, number>()

function shouldIncrementView(jobId: string): boolean {
  const now = Date.now()
  const lastIncrement = recentViewIncrements.get(jobId) ?? 0
  if (now - lastIncrement < 5000) return false
  recentViewIncrements.set(jobId, now)
  return true
}

export default function JobDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const [job, setJob] = useState<JobWithAuthor | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadJob() {
      if (!slug) return

      try {
        setLoading(true)
        const rawJob = await getJobBySlug(slug)

        if (!rawJob) {
          // Fall back to mock data if DB table doesn't exist yet
          const mockJob = MOCK_JOBS.find(j => j.slug === slug)
          if (mockJob) {
            setJob(mockJob)
          } else {
            setError("Job not found")
          }
          return
        }

        const shouldTrackView = shouldIncrementView(rawJob.id)
        const viewPromise = shouldTrackView
          ? incrementJobView(rawJob.id).then(() => true).catch(() => false)
          : Promise.resolve(false)

        const [author, viewIncremented] = await Promise.all([
          getUserById(rawJob.authorId),
          viewPromise,
        ])

        setJob({
          ...rawJob,
          viewCount: rawJob.viewCount + (viewIncremented ? 1 : 0),
          author: author || {
            uid: rawJob.authorId, username: "unknown", displayName: "Unknown User",
            email: "", avatarUrl: "", bio: "", website: "", twitter: "", github: "",
            linkedin: "", reputation: 0, role: "user" as const, createdAt: "", updatedAt: "",
            followerCount: 0, followingCount: 0, postCount: 0, savedPosts: [], bookmarks: [],
          },
        })
      } catch {
        // DB table may not exist yet — try mock data
        const mockJob = MOCK_JOBS.find(j => j.slug === slug)
        if (mockJob) {
          setJob(mockJob)
        } else {
          setError("Job not found")
        }
      } finally {
        setLoading(false)
      }
    }

    loadJob()
  }, [slug])

  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    )
  }

  if (error || !job) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Job Not Found</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">{error || "This job listing doesn't exist."}</p>
          <Link to="/jobs" className="mt-4 inline-block">
            <Button variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Jobs
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const salary = formatSalary(job.salaryMin, job.salaryMax)

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mx-auto max-w-3xl">
        {/* Back link */}
        <Link to="/jobs" className="mb-6 inline-flex items-center text-sm text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Jobs
        </Link>

        {/* Header */}
        <div className="mb-8">
          <p className="text-lg font-medium text-indigo-600 dark:text-indigo-400">{job.companyName}</p>
          <h1 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">{job.title}</h1>

          {/* Badges */}
          <div className="mt-4 flex flex-wrap gap-2">
            <Badge variant="secondary">
              <Briefcase className="mr-1 h-3 w-3" />
              {jobTypeLabels[job.jobType] || job.jobType}
            </Badge>
            {job.location && (
              <Badge variant="secondary">
                <MapPin className="mr-1 h-3 w-3" />
                {job.location}
              </Badge>
            )}
            {job.isRemote && (
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-100">
                <Wifi className="mr-1 h-3 w-3" />
                Remote
              </Badge>
            )}
            {salary && <Badge variant="secondary">{salary}</Badge>}
          </div>

          {/* Meta */}
          <div className="mt-4 flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <span className="flex items-center">
              <Clock className="mr-1 h-4 w-4" />
              Posted {formatRelativeTime(new Date(job.createdAt))}
            </span>
            <span className="flex items-center">
              <Eye className="mr-1 h-4 w-4" />
              {formatNumber(job.viewCount)} views
            </span>
          </div>
        </div>

        {/* Apply CTA */}
        <div className="mb-8 rounded-lg border border-indigo-200 bg-indigo-50 p-6 dark:border-indigo-800 dark:bg-indigo-950/30">
          <p className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Interested in this role?</p>
          <Button asChild size="lg">
            <a href={job.applyUrl} target="_blank" rel="noopener noreferrer">
              Apply Now <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>

        {/* Description */}
        <div className="prose prose-gray max-w-none dark:prose-invert">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">About this role</h2>
          <div className="mt-4 whitespace-pre-wrap text-gray-700 dark:text-gray-300">
            {job.description}
          </div>
        </div>

        {/* Posted by */}
        <div className="mt-10 border-t border-border pt-6">
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">Posted by</p>
          <Link to={`/user/${job.author.username}`} className="flex items-center gap-3 group">
            <Avatar className="h-10 w-10">
              <AvatarImage src={job.author.avatarUrl} alt={job.author.displayName} />
              <AvatarFallback>{getInitials(job.author.displayName)}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-gray-900 group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400">
                {job.author.displayName}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">@{job.author.username}</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  )
}
